import { useState } from "react";
import { Lock, Loader2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Voluntary password change modal — opened from the sidebar "Change password"
 * button. Always requires the user's current password (anti session-stealing).
 *
 * Backend errors:
 *   - 401 → current password wrong
 *   - 400 → new === current
 *   - 422 → new shorter than 8 chars
 */
export default function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Password updated");
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="change-password-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-md w-full p-6 border border-zinc-200"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#002FA7]" />
            <h2 className="text-lg font-bold uppercase tracking-wide">Change Password</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="change-password-close"
            className="text-zinc-400 hover:text-zinc-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block mb-3">
          <span className="overline text-zinc-600">/ current password</span>
          <div className="relative mt-1">
            <input
              type={showCur ? "text" : "password"}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              data-testid="change-password-current"
              autoFocus
              required
              className="w-full px-3 py-2 pr-10 border border-zinc-300 focus:outline-none focus:border-[#002FA7] font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowCur((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              tabIndex={-1}
            >
              {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>

        <label className="block mb-3">
          <span className="overline text-zinc-600">/ new password</span>
          <div className="relative mt-1">
            <input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="change-password-new"
              required
              minLength={8}
              className="w-full px-3 py-2 pr-10 border border-zinc-300 focus:outline-none focus:border-[#002FA7] font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              tabIndex={-1}
            >
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <span className="text-[10px] text-zinc-500 font-mono">Minimum 8 characters</span>
        </label>

        <label className="block mb-5">
          <span className="overline text-zinc-600">/ confirm new password</span>
          <input
            type={showNext ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="change-password-confirm"
            required
            className="mt-1 w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-[#002FA7] font-mono text-sm"
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase font-bold tracking-wider text-zinc-500 hover:text-zinc-800 px-3 py-2"
            data-testid="change-password-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="change-password-submit"
            className="px-4 py-2 bg-[#002FA7] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#00227A] disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Update password
          </button>
        </div>
      </form>
    </div>
  );
}
